import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n } from 'react-redux-i18n-lite';
import * as bookActions from '../../../actions/bookActions';
import * as messageAlert from '../../../actions/messageAlertAction';
import UploadFileContainer from '../../drawer-contents/TaskPanel/UploadFileContainer';
import { AutoExecInstance } from '../../../utils/autoexecutable/autoExecInstance';
import { v4 } from 'uuid';
import { ASSET_TYPES, EXTENSION_POLICY, PARAMS } from '../../../const';
import * as assetsService from '../../../services/assets/assetsService';
import { API_BACKOFFICE_ASSETS } from '../../../services/api/apiConst';
import { InvalidFileNameError } from '../../../utils/autoexecutable/exceptions/invalidFileNameError';
import { FileNotFoundInZipError } from '../../../utils/autoexecutable/exceptions/fileNotFoundInZipError';
import { Modal, Button } from '@netexknowledge/smart-components';
import { ANALYTICS_CONTEXT } from '../../../types/analytics';

class AddLessonAutoExecReveal extends React.Component {
  constructor(context, props) {
    super(context, props);

    this.state = {
      htmlFileName: '',
      file: undefined,
      uploadKey: v4()
    };
  }

  static propTypes = {
    bookActions: PropTypes.any,
    messageAlert: PropTypes.any,
    idClassroom: PropTypes.string,
    selectedUnit: PropTypes.object,
    isAutoExecLessonModalOpen: PropTypes.bool,
    colors: PropTypes.object,
    customerId: PropTypes.number
  };

  cleanForm() {
    this.setState({
      htmlFileName: '',
      file: undefined,
      uploadKey: v4()
    });
  }

  closeModal = () => {
    this.props.bookActions.setIsAutoExecLessonModalOpen(false);
    this.cleanForm();
  };

  handleAddAutoExec = async () => {
    const { idClassroom, selectedUnit, customerId, bookActions } = this.props;
    const { htmlFileName, file } = this.state;

    const finalHtmlFileName = htmlFileName || 'index.html';

    if (finalHtmlFileName && file) {
      this.props.bookActions.setUploadContentStatus({
        percentCompleted: 0,
        isUploading: true,
        cancelUpload: false
      });

      const autoExecInstance = new AutoExecInstance();
      autoExecInstance
        .loadZipFile(file, finalHtmlFileName)
        .then(async () => {
          this.closeModal();
          const defaultUuid = v4();
          const entries = autoExecInstance.getEntries();
          let parsedEntries = 0;
          let assetToCreate = null;

          const handleUploadContentStatus = status => {
            const x = status.percentCompleted;
            const y = parsedEntries;
            const z = entries.length;
            const formula = ((x / 100) * (y / z) + y / z) / 2;
            const globalStatus = {
              percentCompleted: parseInt(`${formula * 100}`),
              isUploading: status.isUploading,
              cancelUpload: status.cancelUpload
            };
            this.props.bookActions.setUploadContentStatus(globalStatus);
          };

          for (const [fileName, zipEntry] of entries) {
            if (!zipEntry.dir) {
              const fileToSave = await autoExecInstance.getFileFromEntry(zipEntry, fileName);
              const shouldCreateAsset = fileName === finalHtmlFileName;

              const assetMetadata = {
                fileName: fileName,
                customerId: customerId,
                assetType: ASSET_TYPES.LESSON
              };

              if (shouldCreateAsset) {
                assetToCreate = {
                  uuid: defaultUuid,
                  fileName: fileName,
                  assetType: ASSET_TYPES.LESSON,
                  mimeType: fileToSave.type
                };
              }

              if (fileToSave) {
                await assetsService
                  .uploadAsset(fileToSave, assetMetadata, {
                    handleUploadContentStatus,
                    createAssetAfterUpload: false,
                    defaultUuid
                  })
                  .catch(error => {
                    const title = autoExecInstance.getTitleByFileName(fileName);
                    this.props.messageAlert.error(`${I18n.t('upload.invalidFilenameOnZip')} ${title}`);
                    bookActions.setUploadContentStatus();
                    throw error;
                  });
                parsedEntries++;
              }
            }
          }

          if (assetToCreate) {
            const asset = await assetsService.createAsset(
              customerId,
              idClassroom,
              defaultUuid,
              assetToCreate.fileName,
              assetToCreate.assetType,
              assetToCreate.mimeType
            );

            const url = API_BACKOFFICE_ASSETS.replace(PARAMS.ASSET, asset.uuid).replace(PARAMS.CUSTOMER, customerId);

            await bookActions.addNewLesson(finalHtmlFileName, url, 'text/html', idClassroom, selectedUnit.activityId, ANALYTICS_CONTEXT.AUTOEXEC_ADD);
          }

          this.props.bookActions.setUploadContentStatus();
          this.props.messageAlert.success(I18n.t('upload.partialSuccess'));

          this.cleanForm();
        })
        .catch(error => {
          this.props.bookActions.setUploadContentStatus();
          if (error instanceof InvalidFileNameError) {
            this.props.messageAlert.error(I18n.t('autoExec.wrongFileExtension'));
          } else if (error instanceof FileNotFoundInZipError) {
            this.props.messageAlert.error(I18n.t('autoExec.fileNotFoundInZip'));
          } else {
            console.error(error);
            this.props.messageAlert.error(I18n.t('upload.error'));
          }
        });
    } else {
      this.props.messageAlert.error(I18n.t('autoExec.mustChooseFile'));
    }
  };

  onAddFile = files => {
    this.setState({
      file: files[0]
    });
  };

  onDelete(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.setState({
      file: undefined
    });
  }

  onChangeName = e => {
    this.setState({
      htmlFileName: e.target.value
    });
  };

  render() {
    const { isAutoExecLessonModalOpen } = this.props;

    return (
      <Modal
        id="new-autoexec"
        title={I18n.t('autoExec.newAutoExec')}
        active={Boolean(isAutoExecLessonModalOpen)}
        onClickOutside={this.closeModal}
        children={
          <>
            <div className="lesson_link">
              <input
                id="add-lesson-auto-exec-html-input"
                type="text"
                placeholder="index.html"
                value={this.state.htmlFileName}
                onChange={this.onChangeName}
              />
            </div>
            <div className="lesson_link">
              <div className="dragAndDrop-wrapper">
                <UploadFileContainer
                  key={this.state.uploadKey}
                  disableClick={this.state.file !== undefined}
                  onDeleteFile={(e, index) => this.onDelete(e, index)}
                  showLoader={false}
                  canChange={true}
                  onDrop={newFiles => this.onAddFile(newFiles)}
                  isInstructor={true}
                  multiple={false}
                  maxFilesPerTask={1}
                  mimetypes={'application/zip,application/x-zip-compressed'}
                  mimetypePolicy={EXTENSION_POLICY.ALLOW}
                />
              </div>
            </div>
          </>
        }
        footer={
          <>
            <Button onClick={this.closeModal} label={I18n.t('common.cancel')}></Button>
            <Button onClick={this.handleAddAutoExec} primary label={I18n.t('common.add')}></Button>
          </>
        }
      ></Modal>
    );
  }
}

function mapStateToProps(state) {
  return {
    isAutoExecLessonModalOpen: state.book.isAutoExecLessonModalOpen,
    i18n: state.i18n,
    selectedUnit: state.book.selectedUnit,
    colors: state.theme.colors,
    customerId: state.account.configuration.customerId
  };
}

function mapDispatchToProps(dispatch) {
  return {
    bookActions: bindActionCreators(bookActions, dispatch),
    messageAlert: bindActionCreators(messageAlert, dispatch),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AddLessonAutoExecReveal);
